"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  AUS_SCHEMES,
  type AusBand,
  type AusScheme,
  type AusSummary,
  ausBand,
  ausBands,
  DEFAULT_AUS_CUTOFFS,
  summariseAus,
  validAusCutoffs,
} from "@/lib/tool-kit/intl-grades/australian-gpa";

/** English strings. A translated content file overrides these through `ui`. */
export const AUSTRALIAN_GPA_CALCULATOR_STRINGS = {
  schemeLabel: "Grade bands",
  schemes: {
    anu: "ANU — HD 80+, D 70–79, C 60–69, P 50–59, fail 0",
    qut: "QUT — HD 85+, D 75–84, C 65–74, P 50–64, fails score 3, 2, 1",
    custom: "Custom cutoffs — type your own",
  },
  schemeHint:
    "Australia has no national scale. Use the bands in your own university's policy; the GPA and WAM change with them.",
  customLegend: "Lowest mark for each grade",
  customHd: "HD from",
  customD: "D from",
  customC: "C from",
  customP: "P from",
  customInvalid: "Cutoffs must be numbers from 1 to 100, falling from HD to P.",
  unitsHeading: "Your units",
  markLabel: "Mark % for unit {n}",
  creditsLabel: "Credit points for unit {n}",
  weightLabel: "WAM weight for unit {n}",
  addUnit: "Add a unit",
  removeUnit: "Remove",
  removeUnitAria: "Remove unit {n}",
  weightHint:
    "WAM weight is 1 unless your university weights units by year level. It affects the WAM only, never the GPA.",
  results: "Result",
  gpa: "GPA (7-point scale)",
  wam: "WAM (weighted average mark)",
  gradeForWam: "Grade band for the WAM",
  creditPoints: "Credit points counted",
  failedPoints: "Credit points failed: {points}",
  grades: {
    HD: "HD — High Distinction",
    D: "D — Distinction",
    C: "C — Credit",
    P: "P — Pass",
    F: "F — Fail",
    F3: "Marginal fail (3)",
    F2: "Fail (2)",
    F1: "Low fail (1)",
  },
  tableHeading: "Unit by unit",
  colUnit: "Unit",
  colMark: "Mark %",
  colGrade: "Grade",
  colPoints: "Grade points",
  colCredits: "Credit points",
  unitName: "Unit {n}",
  skipped: "Skipped (not a mark from 0 to 100, or no credit points): {rows}",
  empty: "Enter at least one mark from 0 to 100 with credit points above zero.",
  caveat:
    "GPA and WAM rules differ between universities: which units count, how a repeated fail is treated and any level weighting. Your faculty's published rule decides the number that appears on your record.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "GPA {gpa} (7-point scale), WAM {wam}",
} as const;

type Strings = typeof AUSTRALIAN_GPA_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type CutoffKey = "hd" | "d" | "c" | "p";

interface Row {
  id: number;
  mark: string;
  credits: string;
  weight: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "88", credits: "6", weight: "1" },
  { id: 2, mark: "76", credits: "6", weight: "1" },
  { id: 3, mark: "69", credits: "6", weight: "1" },
  { id: 4, mark: "58", credits: "6", weight: "1" },
  { id: 5, mark: "42", credits: "6", weight: "1" },
];

const THREE_DECIMALS = { minimumFractionDigits: 3, maximumFractionDigits: 3 };
const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function UnitRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
      <TextField
        id={`aus-mark-${row.id}`}
        label={t(strings.markLabel, { n })}
        numeric
        onChange={(mark) => onChange({ ...row, mark })}
        value={row.mark}
      />
      <TextField
        id={`aus-credits-${row.id}`}
        label={t(strings.creditsLabel, { n })}
        numeric
        onChange={(credits) => onChange({ ...row, credits })}
        value={row.credits}
      />
      <TextField
        id={`aus-weight-${row.id}`}
        label={t(strings.weightLabel, { n })}
        numeric
        onChange={(weight) => onChange({ ...row, weight })}
        value={row.weight}
      />
      <button
        aria-label={t(strings.removeUnitAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeUnit}
      </button>
    </div>
  );
}

function CustomFields({
  raw,
  valid,
  onChange,
  strings,
}: {
  raw: Record<CutoffKey, string>;
  valid: boolean;
  onChange: (key: CutoffKey, value: string) => void;
  strings: Strings;
}) {
  const fields: readonly (readonly [CutoffKey, string])[] = [
    ["hd", strings.customHd],
    ["d", strings.customD],
    ["c", strings.customC],
    ["p", strings.customP],
  ];
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-foreground text-sm">
        {strings.customLegend}
      </legend>
      <div className="grid gap-3 sm:grid-cols-4">
        {fields.map(([key, label]) => (
          <TextField
            id={`aus-custom-${key}`}
            key={key}
            label={label}
            numeric
            onChange={(value) => onChange(key, value)}
            value={raw[key]}
          />
        ))}
      </div>
      {valid ? null : <Notice>{strings.customInvalid}</Notice>}
    </fieldset>
  );
}

function ResultsSection({
  summary,
  tableRows,
  skipped,
  strings,
  fmt,
}: {
  summary: AusSummary | null;
  tableRows: string[][];
  skipped: string[];
  strings: Strings;
  fmt: Format;
}) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary === null ? (
        <Notice>{strings.empty}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.gpa}
              value={fmt(summary.gpa, THREE_DECIMALS)}
            />
            <StatCard
              emphasis
              label={strings.wam}
              value={fmt(summary.wam, TWO_DECIMALS)}
            />
            <StatCard
              label={strings.gradeForWam}
              value={strings.grades[summary.wamBand.grade]}
            />
            <StatCard
              label={strings.creditPoints}
              value={fmt(summary.totalCreditPoints, {
                maximumFractionDigits: 2,
              })}
            />
          </StatGrid>
          {summary.failedCreditPoints > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.failedPoints, {
                points: fmt(summary.failedCreditPoints, {
                  maximumFractionDigits: 2,
                }),
              })}
            </p>
          ) : null}
          {skipped.length > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.skipped, { rows: skipped.join(", ") })}
            </p>
          ) : null}
          <DataTable
            caption={strings.tableHeading}
            head={[
              strings.colUnit,
              strings.colMark,
              strings.colGrade,
              strings.colPoints,
              strings.colCredits,
            ]}
            rows={tableRows}
          />
          <p className="text-muted-foreground text-sm">{strings.caveat}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={t(strings.summary, {
              gpa: fmt(summary.gpa, THREE_DECIMALS),
              wam: fmt(summary.wam, TWO_DECIMALS),
            })}
          />
        </>
      )}
    </section>
  );
}

export default function AustralianGpaCalculatorWidget() {
  const strings = useToolUi(AUSTRALIAN_GPA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [scheme, setScheme] = useState<AusScheme>("anu");
  const [customRaw, setCustomRaw] = useState<Record<CutoffKey, string>>({
    hd: String(DEFAULT_AUS_CUTOFFS.hd),
    d: String(DEFAULT_AUS_CUTOFFS.d),
    c: String(DEFAULT_AUS_CUTOFFS.c),
    p: String(DEFAULT_AUS_CUTOFFS.p),
  });
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(6);

  const cutoffs = {
    hd: parseNumberInput(customRaw.hd) ?? Number.NaN,
    d: parseNumberInput(customRaw.d) ?? Number.NaN,
    c: parseNumberInput(customRaw.c) ?? Number.NaN,
    p: parseNumberInput(customRaw.p) ?? Number.NaN,
  };
  const customOk = scheme !== "custom" || validAusCutoffs(cutoffs);
  const bands: readonly AusBand[] = ausBands(
    scheme,
    customOk ? cutoffs : DEFAULT_AUS_CUTOFFS
  );

  const parsed = rows.map((row) => ({
    mark: parseNumberInput(row.mark),
    credits: parseNumberInput(row.credits),
    weight: parseNumberInput(row.weight) ?? 1,
  }));
  const units = parsed.flatMap(({ mark, credits, weight }) =>
    mark === null || credits === null
      ? []
      : [{ mark, creditPoints: credits, weight }]
  );
  const summary = customOk ? summariseAus(units, bands) : null;
  const skipped = parsed.flatMap(({ mark, credits }, index) =>
    mark !== null && credits !== null && credits > 0 && ausBand(mark, bands)
      ? []
      : [fmt(index + 1)]
  );
  const tableRows = parsed.flatMap(({ mark, credits }, index) => {
    const band = mark === null ? null : ausBand(mark, bands);
    if (mark === null || credits === null || band === null || credits <= 0) {
      return [];
    }
    return [
      [
        t(strings.unitName, { n: fmt(index + 1) }),
        fmt(mark, { maximumFractionDigits: 2 }),
        strings.grades[band.grade],
        fmt(band.points),
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.schemeHint}
          id="aus-scheme"
          label={strings.schemeLabel}
          onChange={setScheme}
          options={AUS_SCHEMES.map((value) => ({
            value,
            label: strings.schemes[value],
          }))}
          value={scheme}
        />
        {scheme === "custom" ? (
          <CustomFields
            onChange={(key, value) =>
              setCustomRaw((current) => ({ ...current, [key]: value }))
            }
            raw={customRaw}
            strings={strings}
            valid={customOk}
          />
        ) : null}
        <h3 className={WIDGET_H3}>{strings.unitsHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <UnitRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <p className="text-muted-foreground text-xs">{strings.weightHint}</p>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", credits: "6", weight: "1" },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addUnit}
        </button>
      </div>
      <ResultsSection
        fmt={fmt}
        skipped={skipped}
        strings={strings}
        summary={summary}
        tableRows={tableRows}
      />
    </div>
  );
}
